import { Reference, StoreObject } from '@apollo/client';
import { TypePolicies } from '@apollo/client/cache/inmemory/policies';
import { Role } from '../types/auth';
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import { CacheType } from '../types/cache';
import { takeBase } from '../components/display/tables/types';
import i18n from '../i18n';

import debug from 'debug';

// Configurar namespaces de debug para la caché de Apollo
const logVehiclesCache = debug('rois:cache:vehicles');

// Habilitar logs solo en desarrollo
if (import.meta.env.DEV) {
  // Habilitar los logs de caché si no están habilitados
  // Esto permite controlar estos logs independientemente de los otros
  debug.enable('rois:cache*');
}

// No añadimos importaciones adicionales para evitar errores

export const policies: TypePolicies = {
  Query: {
    fields: {
      user: {
        read(_, { toReference, variables }) {
          const id = variables?.input?.id ?? variables?.id;
          return id ? toReference({ __typename: 'User', id }) : undefined;
        },
      },
      team: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Team',
            id: variables?.id,
          });
        },
      },
      capitol: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Capitol',
            id: variables?.id,
          });
        },
      },
      subCapitol: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'SubCapitol',
            id: variables?.id,
          });
        },
      },
      classification: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Classification',
            id: variables?.id,
            classificationCode: variables?.classificationCode,
          });
        },
      },
      concession: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Concession',
            id: variables?.id,
          });
        },
      },
      mediaOfCommunication: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'MediaOfCommunication',
            id: variables?.id,
          });
        },
      },
      interlocutorGroup: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'InterlocutorGroup',
            id: variables?.id,
          });
        },
      },
      order: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Order',
            id: variables?.id,
          });
        },
      },
      externalWork: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'ExternalWork',
            id: variables?.id,
          });
        },
      },
      externalCompany: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'ExternalCompany',
            id: variables?.id,
          });
        },
      },

      orders: {
        keyArgs: [
          'input',
          [
            'type',
            'state',
            'states',
            'fromDate',
            'toDate',
            'isMultimedia',
            'classification',
            'capitolId',
            'subCapitolId',
            'predefinedOrderId',
            'indexExpedition',
            'indexType',
            'roadId',
            'concessionId',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'teamId',
            'teamIds',
            'userId',
            'userIds',
            'containerId',
            // Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — filtro por
            // TIPO de origen (pestaña Multimedia del listado del módulo RV),
            // aditivo a containerId (filtro por container concreto,
            // campaña). Misma regla: entra aquí o Apollo sirve caché de otro
            // filtro (ver `project_apollo_keyargs_filter_gotcha`).
            'containerTypes',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente. Lo manda
            // el listado de Multimedia y sus pestañas de Recorridos y Campañas.
            'onlyFlagged',
          ],
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.Orders,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(existing, CacheType.Orders, readField, cursor, take);
        },
      },

      communication: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Communication',
            id: variables?.id,
          });
        },
      },

      communications: {
        keyArgs: [
          'input',
          [
            'roadId',
            'concessionId',
            'fromDate',
            'toDate',
            'classification',
            'predefinedOrderId',
            'indexExpedition',
            'interlocutorGroupId',
            'indexType',
            'state',
            'states',
            'userIds',
            'teamIds',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'containerId',
            // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" en la
            // tabla global de Comunicaciones. Mismo criterio que
            // `actions`/`orders`/`accidents`.
            'containerTypes',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            'onlyFlagged',
          ],
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.Communications,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.Communications,
            readField,
            cursor,
            take
          );
        },
      },

      genericOrder: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'GenericOrder',
            id: variables?.id,
          });
        },
      },
      genericOrders: {
        keyArgs: [
          'input',
          [
            'capitolId',
            'subCapitolId',
            'predefinedOrderId',
            'concessionId',
            'teamId',
            'teamIds',
            'fromDate',
            'toDate',
            'state',
            'states',
            'userIds',
            'classification',
            'indexExpedition',
            'indexType',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'track',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            'onlyFlagged',
            // Ventana de cierre del aviso (spec 2026-11-02 § 4.4): el servidor
            // excluye las cerradas antes de este instante. Sin él en la
            // clave, «Vincular OT» y «Vincular aviso» (misma clave `modal`)
            // comparten cubo y la mezcla conserva filas excluidas.
            'closedNotBefore',
          ],
          '@connection',
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.GenericOrders,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.GenericOrders,
            readField,
            cursor,
            take
          );
        },
      },

      /**
       * Ventana cursor-based para el listado RV (fix 2026-07-20, reporte
       * usuario "next→back→next muerto hasta papelera"). Sin esta policy
       * cada `input` era su propia entrada de caché: el loader hacía
       * readQuery con las variables exactas y la entrada `take=-25`
       * PERSISTIDA (apollo-cache-persist) servía para siempre su pageInfo —
       * con el bug histórico del backend (hasNextPage=false al retroceder)
       * envenenado dentro, la flecha "siguiente" moría hasta la papelera.
       * Con la ventana canónica (patrón genericOrders/campaigns/…):
       * pageInfo se RECALCULA de allItems en cada read (nunca se sirve
       * stale) y el cambio de keyArgs además huérfana las entradas
       * envenenadas ya persistidas.
       */
      // Perf detalle RV (2026-07-21): redirect canónico (patrón `order`/
      // `action`/`vehicleOccupant` de este mismo fichero) para que los
      // `GET_ROUTE_SEGMENT` per-row cache-first del detalle child-orders den
      // hit sobre las entidades `RouteSegment:<id>` que el warm-up plural
      // `GetRouteSegments` acaba de normalizar (MISMO RouteSegmentFragment →
      // paridad byte-exacta, nunca miss parcial).
      routeSegment: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'RouteSegment',
            id: variables?.id,
          });
        },
      },

      surveillanceRoutes: {
        keyArgs: [
          'input',
          [
            'name',
            'roadId',
            'concessionId',
            'teamId',
            'teamIds',
            'operatorIds',
            'userIds',
            'state',
            'states',
            'fromDate',
            'toDate',
            // Rango «Fecha final»: sin él en la keyArg, Apollo serviría en la
            // MISMA lista lo cacheado con otro rango (bug silencioso conocido).
            'fromEndDate',
            'toEndDate',
            'capitolId',
            'subCapitolId',
            'predefinedOrderId',
            'indexExpedition',
            'indexType',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            'onlyFlagged',
          ],
          '@connection',
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.SurveillanceRoutes,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.SurveillanceRoutes,
            readField,
            cursor,
            take
          );
        },
      },

      campaign: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Campaign',
            id: variables?.id,
          });
        },
      },
      campaigns: {
        keyArgs: [
          'input',
          [
            'state',
            'states',
            'userIds',
            'concessionId',
            'fromDate',
            'toDate',
            'name',
            'classification',
            'predefinedOrderId',
            'indexExpedition',
            'indexType',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'teamId',
            'teamIds',
            'track',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente. El
            // modal de campañas (`@connection` «modal») nunca lo manda.
            'onlyFlagged',
            // Ventana de cierre del aviso (spec 2026-11-02 § 4.4): el servidor
            // excluye las cerradas antes de este instante. Sin él en la
            // clave, «Vincular OT» y «Vincular aviso» (misma clave `modal`)
            // comparten cubo y la mezcla conserva filas excluidas.
            'closedNotBefore',
          ],
          '@connection',
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.Campaigns,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.Campaigns,
            readField,
            cursor,
            take
          );
        },
      },

      accident: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Accident',
            id: variables?.id,
          });
        },
      },

      accidents: {
        keyArgs: [
          'input',
          [
            'weatherId',
            'concessionId',
            'fromDate',
            'toDate',
            'mediumKnowledgeId',
            'classification',
            'capitolId',
            'subCapitolId',
            'predefinedOrderId',
            'indexExpedition',
            'indexType',
            'state',
            'states',
            'userIds',
            'teamIds',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'containerId',
            // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" en la
            // tabla global de Accidentes. Mismo criterio que `actions`/`orders`
            // (ver `project_apollo_keyargs_filter_gotcha`).
            'containerTypes',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // Corte de carril: sin esto Apollo mezclaría en la MISMA entrada de
            // caché los resultados filtrados y los sin filtrar.
            'laneCutting',
            // Materiales: mismo motivo. Sin estos dos, Apollo sirve en la MISMA
            // entrada de caché los resultados de dos filtros distintos.
            'materialIds',
            'hasMaterials',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            'onlyFlagged',
          ],
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.Accidents,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.Accidents,
            readField,
            cursor,
            take
          );
        },
      },

      action: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Action',
            id: variables?.id,
          });
        },
      },
      actions: {
        keyArgs: [
          'input',
          [
            'state',
            'states',
            'userIds',
            'operatorIds',
            'teamIds',
            'concessionId',
            'fromDate',
            'toDate',
            'capitolId',
            'subCapitolId',
            'predefinedOrderId',
            'classification',
            'indexExpedition',
            'indexType',
            'externalCompanyId',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'track',
            'activityId',
            'containerId',
            // Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — filtro por
            // TIPO de origen (pestaña Actuacions del listado del módulo RV),
            // aditivo a containerId (filtro por container concreto,
            // campaña).
            'containerTypes',
            'sortField',
            'orderBy',
            // Filtro por descripción: todo filtro nuevo de una lista paginada
            // TIENE que entrar aquí — si no, Apollo sirve en la MISMA lista los
            // resultados cacheados de otra búsqueda.
            'description',
            // Corte de carril: sin esto Apollo mezclaría en la MISMA entrada de
            // caché los resultados filtrados y los sin filtrar.
            'laneCutting',
            // Materiales: mismo motivo. Sin estos dos, Apollo sirve en la MISMA
            // entrada de caché los resultados de dos filtros distintos.
            'materialIds',
            'hasMaterials',
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            'onlyFlagged',
          ],
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.Actions,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.Actions,
            readField,
            cursor,
            take
          );
        },
      },

      actionBudgetItemsHistorical: {
        keyArgs: [
          'input',
          [
            'budgetItemCode',
            'categoryCode',
            'fromDate',
            'toDate',
            'indexExpedition',
            'indexType',
            'concessionId',
            'pkInitKm',
            'pkInitMeter',
            'pkEndKm',
            'pkEndMeter',
            'externalCompanyId',
            'sortField',
            'orderBy',
          ],
        ],
        merge(
          existing = {},
          variables,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return mergeCache(
            existing,
            CacheType.ActionBudgetItems,
            cursor,
            readField,
            take,
            variables
          );
        },
        read(
          existing,
          {
            args: {
              input: { cursor, take = takeBase },
            },
            readField,
          }: any
        ) {
          return readCache(
            existing,
            CacheType.ActionBudgetItems,
            readField,
            cursor,
            take
          );
        },
      },

      activity: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Activity',
            id: variables?.id,
          });
        },
      },
      geoPositionAction: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'GeoPositionAction',
            id: variables?.id,
          });
        },
      },
      geoPositionRoad: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'GeoPositionRoad',
            id: variables?.id,
          });
        },
      },
      /**
       * Perf geo (2026-07-21): GET_POSITION_ROADS quedó recortada a
       * `latitude longitude` (los 4 consumidores v1 solo pintan lat/lng,
       * verificado — actions.ts, LocationForm, MapWithSegments ×2). Sin `id`
       * en la selection los puntos ya NO se normalizan: cada respuesta es un
       * array de objetos embebidos bajo `Query.geoPositionRoads(input)` y un
       * SNAPSHOT completo del trazado decimado (≤200 vértices server-side) →
       * el reemplazo total es el merge correcto y silencia el warning "Cache
       * data may be lost" al reescribir la misma ventana de PKs. Mismo
       * patrón que `SurveillanceRoute.gpsPoints` más abajo. Las entradas
       * viejas persistidas (refs a GeoPositionRoad:<id> con 6 campos) siguen
       * sirviendo la selection recortada (subset) hasta que se reescriban.
       */
      geoPositionRoads: {
        merge(_existing: unknown, incoming: unknown) {
          return incoming;
        },
      },
      asset: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Asset',
            id: variables?.id,
          });
        },
      },
      material: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Material',
            id: variables?.id,
          });
        },
      },
      attentionAccident: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'AttentionAccident',
            id: variables?.id,
          });
        },
      },
      materialUnit: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'MaterialUnit',
            id: variables?.id,
          });
        },
      },
      codification: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Codification',
            id: variables?.id,
          });
        },
      },
      subCodification: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'SubCodification',
            id: variables?.id,
          });
        },
      },
      measureType: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'MeasureType',
            id: variables?.id,
          });
        },
      },
      auxMachine: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'AuxMachine',
            id: variables?.id,
          });
        },
      },
      auxMachineUnit: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'AuxMachineUnit',
            id: variables?.id,
          });
        },
      },
      auxMachineType: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'AuxMachineType',
            id: variables?.id,
          });
        },
      },
      animal: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Animal',
            id: variables?.id,
          });
        },
      },

      animalUnit: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'AnimalUnit',
            id: variables?.id,
          });
        },
      },

      road: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Road',
            id: variables?.id,
          });
        },
      },

      vehicle: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Vehicle',
            id: variables?.id,
          });
        },
      },

      vehicleByPlate: {
        merge(_, incoming, { cache }) {
          if (incoming) {
            const vehicleId = incoming.id;
            if (!vehicleId) return incoming;

            const vehicleRef = cache.identify({
              __typename: 'Vehicle',
              id: vehicleId,
            });

            if (vehicleRef) {
              logVehiclesCache(
                '🔄 Normalizando vehicleByPlate en caché:',
                incoming.plate
              );
              cache.modify({
                id: vehicleRef,
                fields: {
                  plate: () => incoming.plate,
                  model: () => incoming.model,
                  color: () => incoming.color,
                  insurance: () => incoming.insurance,
                  policyNumber: () => incoming.policyNumber,
                  isTrailer: () => incoming.isTrailer,
                  isActive: () => incoming.isActive,
                  isInternal: () => incoming.isInternal,
                  accidentIDs: () => incoming.accidentIDs,
                  vehicleOccupantIDs: () => incoming.vehicleOccupantIDs,
                  parentVehicleIDs: () => incoming.parentVehicleIDs,
                  childVehicleIDs: () => incoming.childVehicleIDs,
                  vehicleCategoryIDs: () => incoming.vehicleCategoryIDs,
                  concessionIDs: () => incoming.concessionIDs,
                  childVehicle: (existing) => incoming.childVehicle || existing,
                },
              });
            }
          }
          return incoming;
        },
      },

      vehicleContextsByAccident: {
        keyArgs: ['accidentId'],
        merge(existing, incoming, { args, variables }) {
          // Obtener accidentId de variables o args
          const accidentId = variables?.accidentId || args?.accidentId;

          if (!incoming || !Array.isArray(incoming)) {
            logVehiclesCache(
              '⚠️ vehicleContextsByAccident: incoming inválido o no es array',
              incoming
            );
            return existing || [];
          }

          logVehiclesCache(
            '🔄 Actualizando vehicleContextsByAccident en caché para accidente:',
            accidentId
          );
          logVehiclesCache(
            `📊 Contextos recibidos: ${incoming.length} contextos`
          );
          logVehiclesCache(
            `📋 Contextos existentes: ${existing?.length || 0} contextos`
          );

          // Para vehicleContextsByAccident siempre queremos la data más reciente
          // No necesitamos merge complejo porque representa el estado actual del accidente
          return incoming;
        },
      },

      vehicleAccidentContext: {
        keyArgs: ['vehicleId', 'accidentId'],
        read(_, { toReference, variables }) {
          // Para contextos individuales, usar la referencia normalizada
          return toReference({
            __typename: 'VehicleAccidentContext',
            vehicleId: variables?.vehicleId,
            accidentId: variables?.accidentId,
          });
        },
      },

      vehicleAccidentHistory: {
        keyArgs: ['vehicleId'],
        merge(existing, incoming, { variables }) {
          const vehicleId = variables?.vehicleId;

          if (!incoming || !Array.isArray(incoming)) {
            logVehiclesCache(
              '⚠️ vehicleAccidentHistory: incoming inválido para vehículo:',
              vehicleId
            );
            return existing || [];
          }

          logVehiclesCache(
            `🔄 Actualizando historial de accidentes para vehículo: ${vehicleId}`
          );
          logVehiclesCache(
            `📊 Contextos históricos recibidos: ${incoming.length} contextos`
          );

          // Para historial siempre queremos la data más reciente
          return incoming;
        },
      },

      // ❌ ELIMINADO DEFINITIVAMENTE: vehiclesForAccident y vehicleForAccident
      // Razón: Las queries vehiclesForAccident y vehicleForAccident ya no existen en el backend
      // El tipo VehicleWithAccidentContext fue eliminado por violar el patrón de arquitectura
      // Ahora usamos GET_VEHICLE_CONTEXTS_BY_ACCIDENT que sigue el patrón correcto de IDs

      vehicleOccupant: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'VehicleOccupant',
            id: variables?.id,
          });
        },
      },

      vehicleOccupantByVehicleId: {
        merge(_, incoming, { cache }) {
          if (incoming) {
            const occupantId = incoming.id;
            if (!occupantId) return incoming;

            const occupantRef = cache.identify({
              __typename: 'VehicleOccupant',
              id: occupantId,
            });

            if (occupantRef) {
              console.log(
                '[cache] normalizando vehicleOccupantByVehicleId',
                occupantId
              );
              cache.modify({
                id: occupantRef,
                fields: {
                  numberOccupants: () => incoming.numberOccupants,
                  userDriverIDs: () => incoming.userDriverIDs,
                  actionIDs: () => incoming.actionIDs,
                  accidentIDs: () => incoming.accidentIDs,
                  vehicleIDs: () => incoming.vehicleIDs,
                },
              });
            }
          }
          return incoming;
        },
      },

      vehicleCategory: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'VehicleCategory',
            id: variables?.id,
          });
        },
      },

      elementOrder: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'ElementOrder',
            id: variables?.id,
          });
        },
      },
      weather: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Weather',
            id: variables?.id,
          });
        },
      },
      mediumKnowledge: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'MediumKnowledge',
            id: variables?.id,
          });
        },
      },

      // Perf resumen accidente (2026-07-21): redirects para los 4 catálogos
      // de accidente que aún no los tenían (weather/mediumKnowledge ya
      // existían arriba; capitol/subCapitol/concession/team/road también).
      // Los singles per-row piden `{id name}` ⊆ `{id name accidentsIDs}` que
      // FETCH_PREDEFINED_OPTIONS_ACCIDENTS normaliza en el loader del
      // detalle/edición (subset verificado query a query) → con el catálogo
      // en caché, la cadena `selectAccidentById`/`fetchAccidentRecord` deja
      // de disparar 1 request por catálogo por accidente. Sin catálogo aún,
      // la referencia queda incompleta y cache-first cae a red = hoy.
      conditionsDriving: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'ConditionsDriving',
            id: variables?.id,
          });
        },
      },
      luminosity: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'Luminosity',
            id: variables?.id,
          });
        },
      },
      surfaceCondition: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'SurfaceCondition',
            id: variables?.id,
          });
        },
      },
      surveillanceBody: {
        read(_, { toReference, variables }) {
          return toReference({
            __typename: 'SurveillanceBody',
            id: variables?.id,
          });
        },
      },

      readMediumKnowledge: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `MediumKnowledge:${id}`,
            })) ?? []
          );
        },
      },

      readCodification: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `Codification:${id}`,
            })) ?? []
          );
        },
      },
      readSubCodification: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `SubCodification:${id}`,
            })) ?? []
          );
        },
      },
      readMeasureType: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `MeasureType:${id}`,
            })) ?? []
          );
        },
      },
      readWeathers: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `Weather:${id}`,
            })) ?? []
          );
        },
      },

      readRoads: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `Road:${id}`,
            })) ?? []
          );
        },
      },

      readOrders: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `Order:${id}`,
            })) ?? []
          );
        },
      },

      /**
       * @client resolver para leer Actions desde cache por IDs.
       *
       * El reto: el type `Action` (GraphQL/Prisma) NO tiene campo `roid` — el ROID
       * vive en la Order paralela. Para que la query `GET_ACTIONS_BY_IDS` pueda
       * devolver `roid` directamente sin que el consumer tenga que hacer un
       * Action→Order lookup manual, lo computamos aquí leyendo el primer
       * `orderIDs[0]` y resolviendo `Order:<id>.roid` via `readField`.
       *
       * Devolvemos un objeto híbrido `{ __ref, roid }`: Apollo usa `__ref` para
       * leer todos los demás fields del fragment NEW_ACTION desde el Action
       * normalizado en cache, y el field `roid` se inyecta como override
       * computado.
       */
      readActions: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;
          const { toReference, readField } = options;

          return (
            variables?.ids
              ?.map((id: string) => {
                const actionRef = toReference({ __typename: 'Action', id });
                if (!actionRef) return null;

                const orderIDs =
                  readField<readonly string[] | undefined>(
                    'orderIDs',
                    actionRef
                  ) || [];
                const orderId = orderIDs[0];
                const orderRef = orderId
                  ? toReference({ __typename: 'Order', id: orderId })
                  : null;
                const orderRoid = orderRef
                  ? readField<string | readonly string[] | null>(
                      'roid',
                      orderRef
                    )
                  : null;
                const roidStr = Array.isArray(orderRoid)
                  ? orderRoid.join('.')
                  : orderRoid ?? null;

                return {
                  __ref: `Action:${id}`,
                  roid: roidStr,
                };
              })
              .filter(Boolean) ?? []
          );
        },
      },

      readCommunications: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `Communication:${id}`,
            })) ?? []
          );
        },
      },

      readSubCapitols: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;

          return (
            variables?.ids?.map((id: string) => ({
              __ref: `SubCapitol:${id}`,
            })) ?? []
          );
        },
      },
      readOperators: {
        read(_, { readField }) {
          const users = readField('users');
          return ((users as []) ?? []).filter(
            (userRef: StoreObject | Reference | undefined) => {
              const userType = readField('userType', userRef);
              const concessionIDs = readField('concessionIDs', userRef);
              // Filtrar solo operarios (userType='operator') con concesión asignada
              // Los operarios NO se filtran por teams
              return (
                userType === 'operator' &&
                (concessionIDs as string[])?.length > 0
              );
            }
          );
        },
      },
      readOperatorsInternals: {
        read(_, { readField }) {
          return ((readField('readOperators') as []) ?? []).filter((operatorRef) =>
            readField('internal', operatorRef)
          );
        },
      },
      readActiveOperators: {
        read(_, { readField }) {
          return ((readField('readOperators') as []) ?? []).filter((operatorRef) => {
            const isActive = readField('isActive', operatorRef);
            const userType = readField('userType', operatorRef);
            // Filtrar solo operadores activos con userType='operator'
            return isActive && userType === 'operator';
          });
        },
      },
      readOperatorsExternals: {
        read(_, { readField }) {
          return ((readField('readOperators') as []) ?? []).filter(
            (operatorRef) => !readField('internal', operatorRef)
          );
        },
      },

      readVehicles: {
        read(_, { readField }) {
          const vehicles = readField('vehicles');
          return vehicles;
        },
      },

      readActiveVehicles: {
        read(_, { readField }) {
          return ((readField('readVehicles') as []) ?? []).filter((vehicleRef) =>
            readField('isActive', vehicleRef)
          );
        },
      },

      readVehicleCategories: {
        read(_, { readField }) {
          const vehicleCategories = readField('vehicleCategories');
          return vehicleCategories;
        },
      },

      readElementOrders: {
        read(_, options) {
          const variables: Record<string, any> | undefined = options.variables;
          return variables?.ids?.map((id: string) => ({
            __ref: `ElementOrder:${id}`,
          }));
        },
      },

      readMaterials: {
        read(_, { readField }) {
          const materials = readField('materials');
          return materials;
        },
      },

      readAnimals: {
        read(_, { readField }) {
          const animals = readField('animals');
          return animals;
        },
      },

      readAuxMachines: {
        read(_, { readField }) {
          const auxMachines = readField('auxMachines');
          return auxMachines;
        },
      },
      readAuxMachinesTypes: {
        read(_, { readField }) {
          const auxMachineTypes = readField('auxMachineTypes');
          return auxMachineTypes;
        },
      },
      readExternalCompanies: {
        /**
         * Catálogo para OFRECER empresa al dar de alta trabajo externo: deja
         * fuera las retiradas.
         *
         * Se filtra AQUÍ y no en el consumidor porque el `read` puede mirar
         * `isActive` de la entidad aunque la query que lo invoca no lo pida —
         * y las dos que lo invocan (`GET_EXTERNAL_COMPANIES`, en los wizards de
         * crear y de validar) seleccionan solo `id` y `companyName`.
         *
         * Ese era el fallo: el desplegable ya filtraba
         * (`ExternalCompaniesSelectionTable`, `isActive !== false`), pero le
         * llegaban objetos SIN el campo, y `undefined !== false` es `true`, así
         * que no descartaba nada. Verificado contra beta: `cetarsa`, retirada y
         * con 14 trabajos, seguía ofreciéndose.
         *
         * No se arregló añadiendo `isActive` a la selección porque eso lo
         * empeora: si la caché tiene la entidad sin ese campo, `readQuery`
         * devuelve `null` para la query ENTERA y el loader revienta al hacer
         * spread — el wizard se queda inservible en vez de mostrar una empresa
         * de más. Lo cazó `validation-external-delete-persists`.
         *
         * `!== false` (no `=== true`) por lo mismo: si el campo no está, se
         * ofrece. Falla ABIERTO, que aquí es lo prudente.
         */
        read(_, { readField }) {
          const externalCompanies = readField('externalCompanies');
          if (!Array.isArray(externalCompanies)) return externalCompanies;
          return externalCompanies.filter(
            (company) => readField('isActive', company) !== false
          );
        },
      },

      // Budget Items policies
      budgetItems: {
        keyArgs: [
          'filters',
          [
            'categoryId',
            'categoryCode',
            'searchTerm',
            'isActive',
            'minPrice',
            'maxPrice',
          ],
          'pagination',
          ['page', 'limit'],
        ],
        merge(existing, incoming, { args }) {
          // Para budget items, siempre queremos la data más reciente del servidor
          // ya que el loader maneja la separación de activos/inactivos
          return incoming;
        },
        read(existing, { args }) {
          // Devolver los datos tal como están en caché
          return existing;
        },
      },

      readBudgetItems: {
        read(_, { readField }) {
          const budgetItemsResult = readField('budgetItems') as {
            items?: any[];
          } | null;
          return budgetItemsResult?.items || [];
        },
      },

      readActiveBudgetItems: {
        read(_, { readField }) {
          const budgetItems = (readField('readBudgetItems') as any[]) ?? [];
          return budgetItems.filter((itemRef) =>
            readField('isActive', itemRef)
          );
        },
      },

      readInactiveBudgetItems: {
        read(_, { readField }) {
          const budgetItems = (readField('readBudgetItems') as any[]) ?? [];
          return budgetItems.filter(
            (itemRef) => !readField('isActive', itemRef)
          );
        },
      },

      budgetCategories: {
        merge(existing, incoming) {
          // Para categorías, siempre usar la data más reciente
          return incoming;
        },
      },

      readBudgetCategories: {
        read(_, { readField }) {
          const budgetCategories = readField('budgetCategories');
          return budgetCategories;
        },
      },

      // Budget Items by Category policies
      budgetItemsByCategory: {
        keyArgs: ['categoryId'],
        merge(existing, incoming, { args }) {
          // Para budget items por categoría, siempre usar la data más reciente
          return incoming;
        },
      },

      // Search Budget Items policies
      searchBudgetItems: {
        keyArgs: ['searchTerm', 'categoryId', 'limit'],
        merge(existing, incoming) {
          // Para búsquedas, siempre usar la data más reciente
          return incoming;
        },
      },

      // Most Used Budget Items policies
      mostUsedBudgetItems: {
        keyArgs: ['limit'],
        merge(existing, incoming) {
          // Para más usados, siempre usar la data más reciente
          return incoming;
        },
      },
    },
  },
  Order: {
    fields: {
      // `closedAt` entró en el fragmento compartido: la caché persistida de
      // antes de actualizar no lo tiene y, sin esto, toda lectura offline de
      // la OT quedaría incompleta (null). Ausente = null.
      closedAt: {
        read(existing) {
          return existing ?? null;
        },
      },
    },
  },
  SurveillanceRoute: {
    fields: {
      /**
       * Track GPS del RV — cada respuesta es un SNAPSHOT completo del
       * track simplificado (Douglas-Peucker), así que el reemplazo total
       * es el merge CORRECTO: declararlo explícito silencia el warning
       * "Cache data may be lost when replacing the gpsPoints field"
       * (2026-07-20; saltaba en cada refetch del detalle y antes también
       * en el flush de addGpsPointsBatch, ya recortado a { id }). Los
       * puntos simplificados no son entidades estables entre snapshots
       * (la simplificación puede elegir puntos distintos) — concatenar
       * sería incorrecto.
       */
      gpsPoints: {
        merge(_existing, incoming) {
          return incoming;
        },
      },
      /**
       * Prevención P1/P3/P4 (2026-10-15): campos que el servidor calcula al
       * leer. 🔴 Si faltan en la caché valen `null`, nunca «incompleto».
       *
       * Sin esto, la caché que la tablet guardó ANTES de actualizarse no los
       * tiene, y Apollo no sirve de su caché nada a lo que le falte un campo:
       * arrancar sin red justo después de la actualización dejaba el
       * cronómetro SIN recorridos activos (el sondeo pide `lastSegmentEndDate`)
       * hasta recuperar cobertura — sin poder cerrar el tramo en curso. Con
       * `null` el recorrido sale igual y lo único que se pierde es el aviso,
       * hasta la siguiente respuesta del servidor.
       */
      shiftWarning: {
        read(existing) {
          return existing ?? null;
        },
      },
      lastSegmentEndDate: {
        read(existing) {
          return existing ?? null;
        },
      },
      // QA T6b: `null` = dato desconocido (caché de antes): quien lo lee cae a
      // la regla prudente de siempre (`hasOwnOpenSegment`).
      openSegmentUserIds: {
        read(existing) {
          return existing ?? null;
        },
      },
    },
  },
  Vehicle: {
    fields: {
      childVehicle: {
        merge(existing, incoming) {
          // Si el incoming es undefined o null, eso significa que el trailer ha sido eliminado
          // así que mantenemos el valor existente para evitar inconsistencias en caché
          if (incoming === undefined || incoming === null) {
            return existing;
          }
          return incoming;
        },
      },
    },
  },
};

function mergeCache(
  existing: any,
  cacheKey: string,
  cursor: string,
  readField: ReadFieldFunction,
  take: number,

  variables: any
) {
  const absoluteTake = Math.abs(take);
  if (cursor === '' && take < 0) take = absoluteTake;

  const newItems: any[] = variables[cacheKey]?.slice() || [];
  if (!newItems.length) {
    console.log(`%c[MERGE] ${cacheKey} cursor=${cursor} take=${take} | NO NEW ITEMS → return existing`, 'color: #ff6600');
    return { ...variables, [cacheKey]: existing?.[cacheKey] || [] };
  }

  // Limpiar nulls de datos existentes (handles legacy cache + trailing null)
  const existingItems: any[] = (existing?.[cacheKey] || []).filter(
    (item: any) => item !== null
  );

  // Sin cursor = carga inicial, reset de filtros, refresh o polling
  if (!cursor) {
    if (existingItems.length === 0) {
      // Primera carga o filtro nuevo (nueva keyArgs → cache vacía) → reemplazar todo
      console.log(`%c[MERGE] ${cacheKey} cursor='' take=${take} | path=REPLACE_ALL | existing=0 new=${newItems.length} → result=${newItems.length} | serverHasNext=${variables.pageInfo?.hasNextPage}`, 'color: #ff6600; font-weight: bold');
      return {
        ...variables,
        [cacheKey]: [...newItems],
      };
    }
    // Refresh (polling/botón) — merge: nuevos al inicio + dedup del resto
    // Preserva cache de páginas posteriores mientras actualiza la primera
    const newRefs = new Set(newItems.map((i: any) => i?.__ref).filter(Boolean));
    const existingWithoutDups = existingItems.filter((i: any) => !newRefs.has(i?.__ref));
    const merged = [...newItems, ...existingWithoutDups];
    console.log(`%c[MERGE] ${cacheKey} cursor='' take=${take} | path=MERGE_DEDUP | existing=${existingItems.length} new=${newItems.length} → merged=${merged.length} (kept ${existingWithoutDups.length} from cache) | serverHasNext=${variables.pageInfo?.hasNextPage}`, 'color: #ff6600; font-weight: bold');
    return {
      ...variables,
      [cacheKey]: merged,
    };
  }

  // Buscar cursor en array existente
  const cursorIdx = existingItems.findIndex(
    (item: any) => item && readField('id', item) === cursor
  );

  let merged: any[];
  let path: string;

  if (cursorIdx === -1) {
    // Cursor no encontrado: salto no-contiguo (ej: última página).
    // NO mezclar — crearía gaps. Apollo sirve datos de red al componente.
    if (existingItems.length > 0) {
      console.log(`%c[MERGE] ${cacheKey} cursor=${cursor.slice(0,8)}… take=${take} | CURSOR_NOT_FOUND | existing=${existingItems.length} items PRESERVED (non-contiguous jump, new=${newItems.length} items served via network)`, 'color: #ff9900; font-weight: bold');
      return existing;
    }
    // Sin datos existentes → almacenar como primera carga
    merged = [...newItems];
    path = 'CURSOR_NOT_FOUND→FRESH_STORE';
  } else if (take > 0) {
    // Forward: mantener todo hasta cursor (incluido), añadir nuevos, dedup suffix
    const prefix = existingItems.slice(0, cursorIdx + 1);
    const existingSuffix = existingItems.slice(cursorIdx + 1);
    const newRefs = new Set(newItems.map((i: any) => i?.__ref).filter(Boolean));
    const cleanSuffix = existingSuffix.filter((i: any) => !newRefs.has(i?.__ref));
    merged = [...prefix, ...newItems, ...cleanSuffix];
    path = `FORWARD cursorIdx=${cursorIdx}`;
  } else {
    // Backward: mantener todo desde cursor, añadir nuevos antes, dedup prefix
    const suffix = existingItems.slice(cursorIdx);
    const existingPrefix = existingItems.slice(0, cursorIdx);
    const newRefs = new Set(newItems.map((i: any) => i?.__ref).filter(Boolean));
    const cleanPrefix = existingPrefix.filter((i: any) => !newRefs.has(i?.__ref));
    merged = [...cleanPrefix, ...newItems, ...suffix];
    path = `BACKWARD cursorIdx=${cursorIdx}`;
  }

  // Deduplicación final por __ref
  const seen = new Set<string>();
  merged = merged.filter((item: any) => {
    if (!item?.__ref) return false;
    if (seen.has(item.__ref)) return false;
    seen.add(item.__ref);
    return true;
  });

  console.log(`%c[MERGE] ${cacheKey} cursor=${cursor.slice(0,8)}… take=${take} | path=${path} | existing=${existingItems.length} new=${newItems.length} cursorIdx=${cursorIdx} → merged=${merged.length} | serverHasNext=${variables.pageInfo?.hasNextPage}`, 'color: #ff6600; font-weight: bold');

  return {
    ...variables,
    [cacheKey]: merged,
  };
}

function readCache(
  existing: any,
  cacheKey: string,
  readField: ReadFieldFunction,
  cursor: string,
  take: number
) {
  const absoluteTake = Math.abs(take);
  if (cursor === '' && take < 0) take = absoluteTake;

  // Limpiar nulls (trailing null de mergeCache + legacy)
  const allItems: any[] = (existing?.[cacheKey] || []).filter(
    (item: any) => item !== null
  );
  const pageInfo = existing?.pageInfo;

  // Cache vacía → miss
  if (allItems.length === 0) {
    console.log(`%c[READ] ${cacheKey} cursor=${cursor ? cursor.slice(0,8)+'…' : "''"} take=${take} | allItems=0 | → MISS (empty cache)`, 'color: red; font-weight: bold');
    return undefined;
  }

  // Sin cursor → primera página
  if (!cursor) {
    const page = allItems.slice(0, Math.min(absoluteTake, allItems.length));
    if (page.length === 0) {
      console.log(`%c[READ] ${cacheKey} cursor='' take=${take} | allItems=${allItems.length} | → MISS (empty page)`, 'color: red; font-weight: bold');
      return undefined;
    }

    const hasNextCalc = allItems.length > absoluteTake || !!pageInfo?.hasNextPage;
    console.log(`%c[READ] ${cacheKey} cursor='' take=${take} | allItems=${allItems.length} | → HIT firstPage page=${page.length} hasNext=${hasNextCalc}`, 'color: #0066ff; font-weight: bold');
    return {
      ...existing,
      [cacheKey]: page,
      pageInfo: {
        ...pageInfo,
        hasPreviousPage: false,
        hasNextPage: hasNextCalc,
      },
    };
  }

  // Buscar cursor en el array
  const cursorIdx = allItems.findIndex(
    (item: any) => readField('id', item) === cursor
  );

  // Cursor no encontrado → miss
  if (cursorIdx === -1) {
    console.log(`%c[READ] ${cacheKey} cursor=${cursor.slice(0,8)}… take=${take} | allItems=${allItems.length} | → MISS (cursor not found)`, 'color: red; font-weight: bold');
    return undefined;
  }

  let start: number, end: number;

  if (take > 0) {
    // Forward: items DESPUÉS del cursor
    start = cursorIdx + 1;
    end = start + absoluteTake;
  } else {
    // Backward: items ANTES del cursor
    end = cursorIdx;
    start = Math.max(0, end - absoluteTake);
  }

  const page = allItems.slice(start, end);

  // Página vacía → miss para forzar fetch
  if (page.length === 0) {
    console.log(`%c[READ] ${cacheKey} cursor=${cursor.slice(0,8)}… take=${take} | allItems=${allItems.length} cursorIdx=${cursorIdx} start=${start} end=${end} | → MISS (empty page)`, 'color: red; font-weight: bold');
    return undefined;
  }

  // Calcular hasNext y hasPrev
  const hasPreviousPage = start > 0;
  let hasNextPage: boolean;

  if (end >= allItems.length) {
    hasNextPage = !!pageInfo?.hasNextPage;
  } else {
    hasNextPage = true;
  }

  // Última página del dataset → siempre fetch fresco del servidor.
  // Pueden haber llegado items nuevos o páginas nuevas desde el último fetch.
  // Apollo retorna los datos de red directamente al client.query(), no re-lee cache.
  if (take > 0 && end >= allItems.length && !hasNextPage) {
    console.log(`%c[READ] ${cacheKey} cursor=${cursor.slice(0,8)}… take=${take} | allItems=${allItems.length} cursorIdx=${cursorIdx} [${start},${end}] | → MISS (last page, force fresh fetch for new data)`, 'color: red; font-weight: bold');
    return undefined;
  }

  console.log(`%c[READ] ${cacheKey} cursor=${cursor.slice(0,8)}… take=${take} | allItems=${allItems.length} cursorIdx=${cursorIdx} [${start},${end}] | → HIT page=${page.length} hasPrev=${hasPreviousPage} hasNext=${hasNextPage} (end>=${allItems.length}?${end >= allItems.length})`, 'color: #0066ff; font-weight: bold');

  return {
    ...existing,
    [cacheKey]: page,
    pageInfo: {
      ...pageInfo,
      hasPreviousPage,
      hasNextPage,
    },
  };
}
